import * as React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useAxios from '../utils/useAxios';
import { NumberField, currency } from './ui';
import { computeFee } from './business/cart';
import type { Order, Settings } from './Orders';

export type CatalogProduct = { id: number; name: string; price: number };
export type ClientOption = { id: number; name: string; lastname?: string; phone?: string };

/** A line as it's being edited: from the order (id), a product of the catalog, or something the catalog doesn't have. */
type Draft = {
  key: string; id?: number; product: number | null; name: string; details: string; quantity: number | null; price: number | null;
  confirmed: boolean; update_price: boolean;
};

let nextKey = 0;
const key = () => `l${++nextKey}`;

export const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

const fromOrder = (order: Order): Draft[] => order.items.map(item => ({
  key: key(), id: item.id, product: item.product ?? null, name: item.name,
  details: [item.options.map(o => o.name).join(', '), item.notes].filter(Boolean).join(' · '),
  // Still to be priced: an empty price, not $0.
  quantity: item.quantity, price: item.confirmed === false && !item.price ? null : item.price,
  // Confirming prices: what's written is what it cost, unless it's still to be priced.
  confirmed: item.confirmed !== false || item.price > 0, update_price: false,
}));

/** Subtotal and fees of the lines being edited, with the business's rules (the server computes the same). */
export const totalsOf = (lines: { quantity: number | null; price: number | null }[], settings: Settings | null, delivery: boolean) => {
  const subtotal = Math.round(lines.reduce((sum, l) => sum + (l.price ?? 0) * (l.quantity ?? 0), 0) * 100) / 100;
  const pieces = lines.reduce((sum, l) => sum + (l.quantity ?? 0), 0);
  const fee = delivery ? computeFee(settings?.delivery_fee, subtotal, pieces) : 0;
  const service = computeFee(settings?.service_fee, subtotal, pieces);
  return { subtotal, fee, service, total: Math.round((subtotal + fee + service) * 100) / 100 };
};

/** Downsized to 1600 px JPEG before uploading: a ticket stays readable and a phone photo stops weighing 5 MB. */
export const shrinkPhoto = (file: File): Promise<Blob> => new Promise(resolve => {
  const image = new Image();
  const url = URL.createObjectURL(file);
  image.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob(blob => resolve(blob ?? file), 'image/jpeg', 0.85);
  };
  image.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  image.src = url;
});

/** The lines: quantity, price and whether it's confirmed; remove one; add a product of the catalog or anything else. */
const LinesEditor = ({ lines, onChange, products, isAdmin, confirming, estimated }: {
  lines: Draft[]; onChange: (lines: Draft[]) => void; products: CatalogProduct[]; isAdmin: boolean;
  /** Confirming what things cost after buying them (offers updating the catalog's price). */
  confirming?: boolean;
  /** The catalog's prices are estimates: a product added starts unconfirmed. */
  estimated?: boolean;
}) => {
  const [adding, setAdding] = React.useState('');
  const set = (k: string, change: Partial<Draft>) => onChange(lines.map(l => (l.key === k ? { ...l, ...change } : l)));
  const add = (value: CatalogProduct | string | null) => {
    if (value == null) return;
    const line: Draft = typeof value === 'string'
      ? { key: key(), product: null, name: value.trim().replace(/\s+/g, ' ').slice(0, 120), details: '', quantity: 1, price: null, confirmed: false, update_price: false }
      : { key: key(), product: value.id, name: value.name, details: '', quantity: 1, price: value.price, confirmed: confirming || !estimated, update_price: false };
    if (line.name.length < 2) return;
    onChange([...lines, line]);
    setAdding('');
  };
  return (
    <Stack spacing={1}>
      {lines.map(line => {
        const catalogPrice = line.product != null ? products.find(p => p.id === line.product)?.price : undefined;
        return (
          <Box key={line.key} data-testid="order-line" sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{line.name}</Typography>
                {line.details && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{line.details}</Typography>}
                {line.product == null && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>No está en tu catálogo</Typography>}
              </Box>
              <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: line.price == null ? 'warning.main' : undefined }}>
                {line.price == null ? 'Por cotizar' : currency(line.price * (line.quantity ?? 0))}
              </Typography>
              <Tooltip title="Quitar (no lo había, ya no lo quiere)">
                <IconButton size="small" aria-label={`Quitar ${line.name}`} onClick={() => onChange(lines.filter(l => l.key !== line.key))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
              <NumberField size="small" label="Cantidad" value={line.quantity} onChange={v => set(line.key, { quantity: v })} sx={{ width: 100 }}
                slotProps={{ htmlInput: { 'data-testid': 'line-quantity' } }} />
              <NumberField size="small" label="Precio c/u" value={line.price} sx={{ width: 130 }}
                onChange={v => set(line.key, { price: v, confirmed: v != null })}
                slotProps={{ htmlInput: { 'data-testid': 'line-price' } }} />
              <FormControlLabel sx={{ mr: 0 }} label={<Typography variant="body2">Precio confirmado</Typography>}
                control={<Checkbox size="small" checked={line.confirmed} onChange={e => set(line.key, { confirmed: e.target.checked })} />} />
              {confirming && isAdmin && line.product != null && line.price != null && catalogPrice !== undefined && catalogPrice !== line.price && (
                <FormControlLabel sx={{ mr: 0 }} label={<Typography variant="body2">Usar {currency(line.price)} en el catálogo (antes {currency(catalogPrice)})</Typography>}
                  control={<Checkbox size="small" checked={line.update_price} onChange={e => set(line.key, { update_price: e.target.checked })} />} />
              )}
            </Stack>
          </Box>
        );
      })}
      <Autocomplete<CatalogProduct, false, false, true>
        freeSolo options={products} inputValue={adding} value={null}
        onInputChange={(_, v, reason) => { if (reason !== 'reset') setAdding(v); }}
        onChange={(_, value) => add(value)}
        getOptionLabel={o => (typeof o === 'string' ? o : o.name)}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <Stack direction="row" sx={{ width: '100%', justifyContent: 'space-between', gap: 2 }}><span>{o.name}</span><span>{currency(o.price)}</span></Stack>
          </li>
        )}
        filterOptions={(options, { inputValue }) => {
          const words = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
          return options.filter(o => words.every(w => o.name.toLowerCase().includes(w))).slice(0, 30);
        }}
        renderInput={params => (
          <TextField {...params} size="small" label="Agregar producto"
            helperText="Busca en tu catálogo, o escribe cualquier cosa y presiona Enter para agregarla."
            slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'add-line' } }} />
        )}
      />
    </Stack>
  );
};

const Totals = ({ lines, settings, delivery }: { lines: Draft[]; settings: Settings | null; delivery: boolean }) => {
  const t = totalsOf(lines, settings, delivery);
  const pending = lines.filter(l => !l.confirmed).length;
  const row = (label: string, value: number, strong = false) => (
    <Stack direction="row" sx={{ justifyContent: 'space-between', fontWeight: strong ? 800 : 400, fontSize: strong ? 18 : undefined }}>
      <span>{label}</span><span>{currency(value)}</span>
    </Stack>
  );
  return (
    <Box data-testid="order-totals" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
      {row('Productos', t.subtotal)}
      {delivery && row('Envío', t.fee)}
      {!!t.service && row(settings?.service_fee_label || 'Comisión por servicio', t.service)}
      {row(pending ? 'Total aproximado' : 'Total', t.total, true)}
      {!!pending && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{pending === 1 ? 'Un precio' : `${pending} precios`} por confirmar.</Typography>}
    </Box>
  );
};

const payloadOf = (lines: Draft[]) => lines.map(l => ({
  ...(l.id != null ? { id: l.id } : l.product != null ? { product: l.product } : { name: l.name }),
  quantity: l.quantity ?? 0, ...(l.price != null ? { price: l.price } : {}), confirmed: l.confirmed, update_price: l.update_price,
}));

/** "Precios": after buying, what each item actually cost; what wasn't there comes off; photos of the purchase ticket. */
export const OrderLinesDialog = ({ order, settings, products, isAdmin, onClose, onSaved }: {
  order: Order; settings: Settings | null; products: CatalogProduct[]; isAdmin: boolean; onClose: () => void; onSaved: (order: Order) => void;
}) => {
  const axios = useAxios();
  const fullScreen = useMediaQuery('(max-width:600px)');
  const [lines, setLines] = React.useState<Draft[]>(() => fromOrder(order));
  const [photos, setPhotos] = React.useState(order.photos ?? []);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileRef = React.useRef<HTMLInputElement>(null);

  const save = async () => {
    if (lines.length === 0) { setError('El pedido necesita al menos un producto. Para quitarlo todo, cancélalo.'); return; }
    if (lines.some(l => !l.quantity)) { setError('Revisa las cantidades.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.put(`api/orders/${order.uuid}/lines/`, { items: payloadOf(lines) });
      onSaved(data);
    } catch (err) {
      setError(errorText(err, 'No se pudieron guardar los precios.'));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', await shrinkPhoto(file), 'ticket.jpg');
      const { data } = await axios.post(`api/orders/${order.uuid}/photos/`, form);
      setPhotos(data.photos ?? []);
    } catch (err) {
      setError(errorText(err, 'No se pudo subir la foto.'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const removePhoto = async (id: number) => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`api/orders/${order.uuid}/photos/${id}/`);
      setPhotos(data.photos ?? []);
    } catch (err) {
      setError(errorText(err, 'No se pudo quitar la foto.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Precios del pedido {order.folio}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{order.customer_name}</Typography>
        <IconButton aria-label="Cerrar" onClick={onClose} disabled={busy} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Escribe lo que costó cada producto al comprarlo. Lo que no encontraste, quítalo. El total y la comisión se recalculan solos.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
        <LinesEditor lines={lines} onChange={setLines} products={products} isAdmin={isAdmin} confirming />
        <Box sx={{ mt: 2 }}><Totals lines={lines} settings={settings} delivery={order.fulfillment === 'delivery'} /></Box>
        <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>Ticket de compra</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {photos.map(photo => (
            <Box key={photo.id} sx={{ position: 'relative' }}>
              <Box component="a" href={photo.url} target="_blank" rel="noopener noreferrer">
                <Box component="img" src={photo.url} alt="Ticket de compra" data-testid="order-photo"
                  sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', display: 'block' }} />
              </Box>
              <IconButton size="small" aria-label="Quitar foto" onClick={() => removePhoto(photo.id)} disabled={busy}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
          <Button variant="outlined" startIcon={<AddPhotoAlternateOutlinedIcon />} onClick={() => fileRef.current?.click()} disabled={busy}
            sx={{ height: 84 }}>
            Foto del ticket
          </Button>
          <input ref={fileRef} type="file" accept="image/*" hidden data-testid="photo-input" onChange={e => upload(e.target.files?.[0])} />
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
          Solo tu equipo la ve; tu cliente no.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy} data-testid="save-lines">{busy ? 'Guardando…' : 'Guardar precios'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** "Nuevo pedido": an order the business takes for a customer (by WhatsApp, in person…). */
export const NewOrderDialog = ({ settings, products, clients, isAdmin, onClose, onCreated }: {
  settings: Settings | null; products: CatalogProduct[]; clients: ClientOption[]; isAdmin: boolean; onClose: () => void; onCreated: (order: Order) => void;
}) => {
  const axios = useAxios();
  const fullScreen = useMediaQuery('(max-width:600px)');
  const [client, setClient] = React.useState<ClientOption | null>(null);
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [fulfillment, setFulfillment] = React.useState<'pickup' | 'delivery'>('pickup');
  const [address, setAddress] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [lines, setLines] = React.useState<Draft[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const fullName = (c: ClientOption) => [c.name, c.lastname].filter(Boolean).join(' ');

  const create = async () => {
    if (!client && !name.trim()) { setError('Escribe el nombre del cliente.'); return; }
    if (lines.length === 0) { setError('Agrega lo que pidió.'); return; }
    if (lines.some(l => !l.quantity)) { setError('Revisa las cantidades.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post('api/orders/', {
        ...(client ? { client: client.id } : {}), customer: { name: client ? fullName(client) : name, phone: client ? '' : phone },
        fulfillment, delivery_address: fulfillment === 'delivery' ? address : '', notes, items: payloadOf(lines),
      });
      onCreated(data);
    } catch (err) {
      setError(errorText(err, 'No se pudo crear el pedido.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Nuevo pedido
        <IconButton aria-label="Cerrar" onClick={onClose} disabled={busy} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Autocomplete<ClientOption, false, false, true>
            freeSolo options={clients} value={client}
            getOptionLabel={c => (typeof c === 'string' ? c : `${fullName(c)}${c.phone ? ` · ${c.phone}` : ''}`)}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase().replace(/\s+/g, ' ').trim();
              const digits = q.replace(/\D/g, '');
              return q ? options.filter(c => fullName(c).toLowerCase().includes(q) || (digits.length >= 3 && (c.phone ?? '').replace(/\D/g, '').includes(digits))).slice(0, 20) : [];
            }}
            onChange={(_, value) => { if (typeof value !== 'string') setClient(value); }}
            onInputChange={(_, value, reason) => { if (reason === 'input') { setClient(null); setName(value); } }}
            renderInput={params => (
              <TextField {...params} label="Cliente" helperText={client ? 'Cliente registrado.' : 'Busca un cliente o escribe el nombre de uno nuevo.'}
                slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'order-customer' } }} />
            )}
          />
          {!client && (
            <TextField label="Celular (opcional)" value={phone} onChange={e => setPhone(e.target.value)} type="tel"
              helperText="Lo registra como cliente y te deja mandarle su total por WhatsApp."
              slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 20, 'data-testid': 'order-phone' } }} />
          )}
          <ToggleButtonGroup exclusive value={fulfillment} onChange={(_, v) => v && setFulfillment(v)} size="small">
            <ToggleButton value="pickup">Recoge</ToggleButton>
            <ToggleButton value="delivery">A domicilio</ToggleButton>
          </ToggleButtonGroup>
          {fulfillment === 'delivery' && (
            <TextField label="Dirección de entrega" value={address} onChange={e => setAddress(e.target.value)} multiline minRows={2}
              slotProps={{ htmlInput: { maxLength: 300 } }} />
          )}
          <LinesEditor lines={lines} onChange={setLines} products={products} isAdmin={isAdmin} estimated={!!settings?.estimated_prices} />
          {lines.length > 0 && <Totals lines={lines} settings={settings} delivery={fulfillment === 'delivery'} />}
          <TextField label="Notas (opcional)" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={1}
            slotProps={{ htmlInput: { maxLength: 500 } }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={create} disabled={busy} data-testid="create-order">{busy ? 'Creando…' : 'Crear pedido'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** The order's total, line by line, ready to send by WhatsApp with the link to follow it. */
export const totalMessage = (order: Order) => {
  const lines = order.items.map(i => `• ${i.quantity} × ${i.name}: ${i.confirmed === false && !i.price ? 'por cotizar' : currency(i.price * i.quantity)}`);
  const first = order.customer_name.split(' ')[0];
  return [
    `Hola${first ? ` ${first}` : ''}, tu pedido ${order.folio}:`,
    ...lines,
    `Productos: ${currency(order.subtotal)}`,
    ...(order.fee ? [`Envío: ${currency(order.fee)}`] : []),
    ...(order.service_fee ? [`${order.service_fee_label || 'Comisión por servicio'}: ${currency(order.service_fee)}`] : []),
    `${order.pending_prices ? 'Total aproximado' : 'Total'}: ${currency(order.amount)}`,
    ...(order.track_path ? [`Detalle: ${window.location.origin}${order.track_path}`] : []),
  ].join('\n');
};
